import React from 'react';
import './style.css';
import cometchatLogo from '../../../assets/cometchatLogo.svg';
import { ShareAltOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Button, Dropdown, Tooltip } from 'antd';
import androidIcon from '../../../assets/androidIcon.svg';
import reactIcon from '../../../assets/reactIcon.svg';
import iosIcon from '../../../assets/iosIcon.svg';
import syncIcon from '../../../assets/syncIcon.svg';

/**
 * Props for the Header component.
 */
interface HeaderProps {
  /** Callback function triggered when fullscreen mode is toggled. */
  handleFullScreen?: () => void;
  /** Callback function triggered when the share button is clicked. */
  handleShare?: () => void;
  /** Callback function when any value updates*/
  onSave?: () => void;
  /** Logo image source for the header. */
  logo?: string;
  /** Text content displayed on the download button. */
  textContent?: string;
  /** Indicates whether the component is in preview mode. */
  isPreviewScreen?: boolean;
  /** Determines whether the user is authenticated. */
  isAuthenticated?: boolean;
  /** Indicates whether the data has been saved. */
  isDataSaved?: boolean;
  /** Callback function triggered when the dashboard button is clicked. */
  onDashboardClick?: () => void;
  /** Callback function triggered when the reset button is clicked. */
  onResetClick?: () => void;
}

/**
 * Header component displaying navigation, sharing, and saving options.
 *
 * @param {HeaderProps} props - Component props.
 * @returns {JSX.Element} The rendered Header component.
 */
const Header: React.FC<HeaderProps> = ({
  handleFullScreen = () => {},
  handleShare = () => {},
  onSave = () => {},
  logo = cometchatLogo,
  textContent = 'Download',
  isPreviewScreen = true,
  isAuthenticated = false,
  isDataSaved = false,
  onDashboardClick,
  onResetClick,
}) => {
  const items: MenuProps['items'] = [
    {
      label: <div className="menuItem react">React Code</div>,
      key: '1',
      icon: <img src={reactIcon} alt="React Icon" style={{ width: '24px', height: '24px' }} />,
      onClick: () => onSave(),
    },
    {
      label: (
        <div className="menuItem android">
          Android Code <span className="comingSoonText">Coming Soon</span>
        </div>
      ),
      key: '2',
      icon: <img src={androidIcon} alt="Android Icon" style={{ width: '24px', height: '24px' }} />,
    },
    {
      label: (
        <div className="menuItem ios">
          iOS Code <span className="comingSoonText">Coming Soon</span>
        </div>
      ),
      key: '3',
      icon: <img src={iosIcon} alt="iOS Icon" style={{ width: '24px', height: '24px' }} />,
    },
  ];

  const buttonView =
    isPreviewScreen && !isAuthenticated ? (
      <>
        <div className="iconBtnHeader">
          <Button
            title="Share site"
            icon={<ShareAltOutlined />}
            style={{ height: '32px', width: '40px' }}
            onClick={handleShare}
          />
        </div>

        <Button iconPosition="end" onClick={() => onSave()} className="downloadButton">
          <span className="downloadText">{textContent} </span>
        </Button>
      </>
    ) : (
      <>
        <Dropdown placement="bottomLeft" menu={{ items }}>
          <Button className="downloadButton">
            <span className="downloadText">{textContent}</span>
            <span className="verticalDivider" />
            <span>
              <div className="downloadExpandIcon" />
            </span>
          </Button>
        </Dropdown>
      </>
    );

  return (
    <div id="headerWrapper" className="headerWrapper">
      <div className="headerLeftSection">
        <div className="cometchatTitle">
          <img src={logo} alt="" />
        </div>
      </div>
      <div className="headerRightSection">
        {(!isPreviewScreen || isAuthenticated) && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {isAuthenticated && (
              <>
                <Tooltip title={isDataSaved ? 'Changes saved' : 'Unsaved changes'}>
                  <Button className={`synchOrCheckButton ${isDataSaved ? 'checkedButton' : 'syncButton'} `}>
                    {isDataSaved ? (
                      <div className="checkIcon" />
                    ) : (
                      <div className="syncIconWrapper">
                        <img src={syncIcon} /> Saving Changes
                      </div>
                    )}
                  </Button>
                </Tooltip>

                <Tooltip title={'Reset to default'}>
                  <div className="iconBtnHeader">
                    <Button style={{ width: '40px' }} onClick={onResetClick}>
                      <div className="headerBtnIcon headerBtnReloadIcon" />
                    </Button>
                  </div>
                </Tooltip>
              </>
            )}

            <div className="iconBtnHeader">
              <Button style={{ width: '40px' }} onClick={handleFullScreen}>
                <div className="headerBtnIcon headerBtnEyeIcon" />
              </Button>
            </div>
            {isAuthenticated && (
              <Button style={{ height: '32px' }} className="dashboardButton" onClick={onDashboardClick}>
                <span className="dashboardText">Dashboard</span>
              </Button>
            )}
          </div>
        )}
        <div className="shareAndCustomizeBtnWrapper">{buttonView}</div>
      </div>
    </div>
  );
};

export default Header;
